import { useMemo } from "react"
import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import Table from "components/material/table"
import { map, pick, replace, toLower, get } from "lodash"
import { DATE, UUID, ROLE } from "helpers/shapes"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import { getAliasErrorMessage, isAliasErrorMessage } from "./helpers"
import { isAllAccessTeam } from "helpers/permissions/teams"

export default function Teams({ account, role, teams, update, tabs }) {
  const addForm = useMemo(() => {
    function onAdd(data, cb) {
      const query = `mutation ($name: String!, $alias: Alias!, $description: String) {
        createTeam(team: { name: $name, alias: $alias, description: $description }) { id }
      }`

      const variables = {
        ...data,
        alias: replace(toLower(data.name), /[\s-]/g, "_"),
      }

      update(query, variables, account, {
        reloadData: "teams",
      }, (message, error) => {
        const errorMessage = get(error, "error.0.message")

        let parsedMessage
        if (isAliasErrorMessage(errorMessage)) {
          parsedMessage = getAliasErrorMessage(errorMessage)
        }

        cb(parsedMessage || message)
      })
    }

    return {
      title: "Create Team",
      submitText: "Create Team",
      track: "new_team_created",
      onSubmit: onAdd,
      fields: [{
        id: "name",
        label: "Name",
        type: "string",
        validations: {
          isRequired: true,
          minLength: 3,
          maxLength: 50,
        },
      }, {
        id: "alias",
        label: "Alias",
        type: "alias",
        on: "name",
      }, {
        id: "description",
        label: "Description",
        type: "text",
      }],
    }
  }, [account, update])

  const editForm = useMemo(() => {
    function onEdit(data, cb) {
      const query = `mutation ($id: IdOrAlias!, $name: String!, $description: String) {
        updateTeam(id: $id, team: { name: $name, description: $description }) { id }
      }`

      const variables = {
        ...data,
        description: data.description || null,
      }

      update(query, variables, account, {
        reloadData: "teams",
      }, cb)
    }

    return {
      title: "Edit Team",
      submitText: "Save",
      track: {
        type: "edit_team_info",
        payload: data => pick(data, "name", "description"),
      },
      onSubmit: onEdit,
      getInitialValues: ({ name, description }) => ({ name, description }),
      isEnabled: (team) => !isAllAccessTeam(team.alias) && (role === "admin" || team.role === "ADMIN"),
      fields: [{
        id: "name",
        label: "Name",
        type: "string",
        validations: {
          isRequired: true,
          minLength: 3,
          maxLength: 50,
        },
      }, {
        id: "description",
        label: "Description",
        type: "text",
      }],
    }
  }, [account, role, update])

  const deleteForm = useMemo(() => {
    function onDelete(data, cb) {
      const query = `mutation {
        ${map(data, (team, index) => `team${index}: deleteTeam (id: "${team.id}") { ok }`)}
      }`

      update(query, null, account, {
        // We also clear any loaded team
        reloadData: ["teams", "teamMembers", "teamPermissions"],
      }, cb)
    }

    return {
      type: "team",
      track: {
        type: "delete_team_confirmed",
        payload: ({ name }) => ({ resource: name }),
      },
      isEnabled: (team) => !isAllAccessTeam(team.alias) && (role === "admin" || team.role === "ADMIN"),
      renderItem: team => <li key={team.id}>{team.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, role, update])

  return <div className="module_teams">
    <UpsellTabs id="teams" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Teams"
        idField="id"
        addForm={addForm}
        editForm={editForm}
        deleteForm={deleteForm}
        data={teams}
        columns={[{
          name: "Name",
          id: "name",
          linkTo: (team) => `/${account}/home/teams/${team.alias}`,
        }, {
          name: "Description",
          id: "description",
        }]}
      />
    </Paper>
  </div>
}

Teams.propTypes = {
  account: PropTypes.string.isRequired,
  role: ROLE.isRequired,
  teams: PropTypes.arrayOf(PropTypes.shape({
    id: UUID.isRequired,
    alias: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    createdAt: DATE.isRequired,
    updatedAt: DATE.isRequired,
    role: PropTypes.oneOf(["ADMIN", "MEMBER"]),
  })),
  update: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
